/**
 * 主线程 Worker 宿主（任务 6c）：包住 Worker + RpcClient，提供跨「崩溃重建」
 * 存活的 call/onPush——docs/worker-arch.md「Worker 崩溃重启」的落点。
 *
 * - worker `error` 事件（未捕获异常）→ dispose 旧 client（在途调用以
 *   rpc/disposed 拒绝，调用方自行决定重试）→ terminate → spawn 新 Worker →
 *   重挂全部推送订阅 → onRestart（appWorker 用它重新 sync 鉴权态）。
 *   Worker 无内存态依赖（事实源在 IndexedDB），直接重建是安全的。
 * - 连续崩溃超过 maxRestarts 次不再重建（避免崩溃风暴空转），host 进入死亡态：
 *   后续 call 一律以 rpc/disposed 拒绝。
 * - onPush 订阅登记在 host 自己的 registry，重建后自动转挂新 client。
 */

import { createRpcClient, type RpcClient } from '~/worker/rpc/client';
import type { MethodMap, PushMap } from '~/worker/rpc/protocol';

/** Worker 的最小结构形状（真 Worker 满足；测试用 MessageChannel 假体） */
export interface WorkerLike {
  postMessage(message: unknown, transfer?: Transferable[]): void;
  addEventListener(type: 'message', listener: (ev: MessageEvent) => void): void;
  addEventListener(type: 'error', listener: (ev: unknown) => void): void;
  removeEventListener(type: 'message', listener: (ev: MessageEvent) => void): void;
  removeEventListener(type: 'error', listener: (ev: unknown) => void): void;
  terminate(): void;
}

export interface WorkerHostOptions {
  spawn(): WorkerLike;
  /** 崩溃重建完成后回调（推送订阅已转挂；用于重新拉取快照类状态） */
  onRestart?(): void;
  /** 累计重建上限，超过进入死亡态。默认 5 */
  maxRestarts?: number;
}

export interface WorkerHost<M extends MethodMap, P extends PushMap> {
  call: RpcClient<M, P>['call'];
  onPush: RpcClient<M, P>['onPush'];
  dispose(): void;
}

export function createWorkerHost<M extends MethodMap, P extends PushMap>(
  options: WorkerHostOptions,
): WorkerHost<M, P> {
  const maxRestarts = options.maxRestarts ?? 5;
  /** 事件 → 订阅者。跨重建的事实源；client 上只挂一个转发器 */
  const registry = new Map<string, Set<(payload: unknown) => void>>();
  let restarts = 0;
  let dead = false;
  let worker: WorkerLike;
  let client: RpcClient<M, P>;
  let offError: () => void;

  function forwardTo(event: string): void {
    client.onPush(event as keyof P & string, (payload) => {
      const handlers = registry.get(event);
      if (!handlers) return;
      for (const handler of [...handlers]) handler(payload);
    });
  }

  function attach(): void {
    worker = options.spawn();
    client = createRpcClient<M, P>(worker);
    const onError = (): void => restart();
    worker.addEventListener('error', onError);
    offError = () => worker.removeEventListener('error', onError);
    for (const event of registry.keys()) forwardTo(event);
  }

  function teardown(): void {
    offError();
    client.dispose();
    worker.terminate();
  }

  function restart(): void {
    if (dead) return;
    teardown();
    if (restarts >= maxRestarts) {
      dead = true; // client 保持 disposed 态，后续 call 以 rpc/disposed 拒绝
      return;
    }
    restarts += 1;
    attach();
    options.onRestart?.();
  }

  attach();

  return {
    // 不能直接引用 client 的方法：重建后要落在新 client 上
    call: (method, payload, callOptions) => client.call(method, payload, callOptions),

    onPush(event, handler) {
      let handlers = registry.get(event);
      if (!handlers) {
        handlers = new Set();
        registry.set(event, handlers);
        forwardTo(event);
      }
      const h = handler as (payload: unknown) => void;
      handlers.add(h);
      return () => {
        handlers.delete(h);
      };
    },

    dispose() {
      if (dead) return;
      dead = true;
      teardown();
    },
  };
}
